
import { useState, useCallback, useEffect } from 'react';
import { WebhookInfo } from './types';
import { useToast } from '@/hooks/use-toast';
import { getWebhookInfo, setupWebhook, sendTelegramMessage, testConnection } from '@/services/directTelegram/api';
import { isSystemBotTokenConfigured } from '@/services/telegram/botTokenService';

// Local storage key for custom webhook URL
export const WEBHOOK_URL_KEY = "telegram_webhook_url";

export const useDirectWebhookStatus = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState<WebhookInfo | null>(null);
  const [isTestingSendMessage, setIsTestingSendMessage] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const { toast } = useToast();

  const checkWebhookStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const isConfigured = await isSystemBotTokenConfigured();
      
      if (!isConfigured) {
        toast({
          title: "Bot Token Required",
          description: "Please set up your bot token first",
          variant: "destructive",
        });
        setWebhookInfo(null);
        return;
      }
      
      const webhookInfoResponse = await getWebhookInfo();
      
      if (webhookInfoResponse) {
        setWebhookInfo(webhookInfoResponse);
      } else {
        setWebhookInfo(null);
      }
    } catch (error: any) {
      console.error("Failed to check webhook info:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to check webhook info",
        variant: "destructive",
      });
      setWebhookInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    // Check webhook status on first load if bot token exists
    const checkConfig = async () => {
      const isConfigured = await isSystemBotTokenConfigured();
      if (isConfigured) {
        checkWebhookStatus();
      }
    };
    
    checkConfig();
  }, [checkWebhookStatus]);

  const setupWebhookHandler = useCallback(async () => {
    setIsLoading(true);
    try {
      const isConfigured = await isSystemBotTokenConfigured();
      
      if (!isConfigured) {
        toast({
          title: "Bot Token Required",
          description: "Please set up your bot token first",
          variant: "destructive",
        });
        return;
      }
      
      // Use stored custom webhook URL if available
      const customWebhookUrl = localStorage.getItem(WEBHOOK_URL_KEY);
      
      if (!customWebhookUrl) {
        toast({
          title: "Webhook URL Required",
          description: "No webhook URL found. Please enter a URL when prompted.",
          variant: "destructive",
        });
        // The actual URL input is handled by WebhookTestControls component
        setIsLoading(false);
        return;
      }
      
      const result = await setupWebhook(customWebhookUrl);
      
      if (result.success) {
        toast({
          title: "Webhook Setup Successful",
          description: `Webhook set to: ${customWebhookUrl}`,
        });
        
        // Refresh webhook status
        checkWebhookStatus();
      } else {
        toast({
          title: "Webhook Setup Failed",
          description: result.error || "Failed to setup webhook",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Failed to setup webhook:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to setup webhook",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, checkWebhookStatus]);

  const testSendMessage = useCallback(async () => {
    setIsTestingSendMessage(true);
    try {
      const isConfigured = await isSystemBotTokenConfigured();
      
      if (!isConfigured) {
        toast({
          title: "Bot Token Required",
          description: "Please set up your bot token first",
          variant: "destructive",
        });
        return;
      }
      
      // Read chat IDs from local storage
      const chatIdsStr = localStorage.getItem('telegram_chat_ids');
      let chatIds: number[] = [];
      
      if (chatIdsStr) {
        try {
          chatIds = JSON.parse(chatIdsStr);
        } catch (e) {
          console.error('Invalid stored chat IDs');
        }
      }
      
      if (chatIds.length === 0) {
        toast({
          title: "No Recipients",
          description: "Please add chat IDs in the Bot Token tab first",
          variant: "destructive",
        });
        return;
      }
      
      // Send test message to first chat ID
      const testChatId = chatIds[0];
      const message = "🧪 This is a test message from your Telegram bot.";
      
      const success = await sendTelegramMessage(testChatId, message);
      
      if (success) {
        toast({
          title: "Test Message Sent",
          description: `Message sent to chat ID: ${testChatId}`,
        });
      } else {
        toast({
          title: "Failed to Send Message",
          description: "The message could not be sent. Check your bot token and chat ID.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Failed to send test message:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test message",
        variant: "destructive",
      });
    } finally {
      setIsTestingSendMessage(false);
    }
  }, [toast]);

  const testConnectionHandler = useCallback(async () => {
    setIsTestingConnection(true);
    try {
      const result = await testConnection();
      
      if (result.success) {
        toast({
          title: "Connection Successful",
          description: result.message || "Successfully connected to Telegram API",
        });
      } else {
        toast({
          title: "Connection Failed",
          description: result.message || "Failed to connect to Telegram API",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Failed to test connection:", error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsTestingConnection(false);
    }
  }, [toast]);

  return {
    isLoading,
    webhookInfo,
    isTestingSendMessage,
    isTestingConnection,
    checkWebhookStatus,
    setupWebhook: setupWebhookHandler,
    testSendMessage,
    testConnection: testConnectionHandler
  };
};
